import * as React from 'react';
import { Checkbox, IconFilters16, MultiSelect, UIHeading5, UiKitContextProvider } from '@cian/ui-kit';
import { mergeStyles } from '@cian/utils';
import { formatQuarterDate } from '../../../utils/dropdown';
import { TMobileFiltersProps } from './container';
import { MobileLocationSwitcherContainer } from './location_switcher/container';
import { MobileFiltersPopup } from './popup';
import * as styles from './index.css';
import { FilterItemMobile } from './components/FilterItemMobile';
import {
  FINISH_DATES,
  OBJECT_TYPES,
  VILLAGE_CLASSES,
  SALE_STATUSES,
  READY_STATUSES,
  HOUSE_MATERIALS,
  HEATING_SOURCES,
  OTHER_COMMUNICATIONS,
  WC_LOCATIONS,
} from '../../../constants/filters';
import { SelectMobile } from './components/SelectMobile';
import { RangeInput } from '../../RangeInput';
import { generateDistanceTypes } from '../../../containers/filters/DistanceContainer/utils';
import { RadioButtonGroup } from '../../RadioButtonGroup';
import { ECommuteDistanceType } from '../../../types/filters';
import { CheckboxButtonGroup } from '../../CheckboxButtonGroup';
import blackFridayIcon from './assets/blackFridayIcon.svg';

export class MobileFilters extends React.Component<TMobileFiltersProps> {
  public componentDidMount() {
    this.props.onShowFiltersButton();
    this.props.fetchRegions();
  }

  public render() {
    const {
      areFiltersOpened,
      filters: {
        areaFrom,
        areaTo,
        commuteDistanceType,
        commuteKmFrom,
        commuteKmTo,
        finishDate,
        floorCountFrom,
        floorCountTo,
        heatingSourceType,
        houseMaterialType,
        newobjectType,
        otherCommunications,
        priceFrom,
        priceTo,
        readyStatus,
        regionId,
        saleStatus,
        villageClass,
        wcLocationType,
        blackFriday,
      },
      showClearButton,
      showRoadFilters,
      applyFilters,
      clearFilters,
      changeAreaRange,
      changeCommuteDistance,
      changeCommuteDistanceType,
      changeNewObjectTypes,
      changeVillageClasses,
      changeFinishDate,
      changeFloorCountRange,
      changeHeatingSourceType,
      changeHouseMaterials,
      changeOtherCommunications,
      changePriceRangeFilter,
      changeSaleStatus,
      changeReadyStatus,
      changeWcLocationType,
      changeBlackFridayFilter,

      className,
      style,
      showBlackFridayFilter,
    } = this.props;

    return (
      // Необходимо, чтобы не менялось поведение на планшете
      <UiKitContextProvider useStaticDeviceType={true} deviceType="phone">
        <div
          aria-label="Открыть фильтры"
          {...mergeStyles(styles['container'], className, style)}
          onClick={this.openPopup}
        >
          <IconFilters16 color="primary_100" />
          <div className={styles['title']}>
            <UIHeading5 color="primary_100">Фильтры</UIHeading5>
          </div>
        </div>
        <MobileFiltersPopup
          open={!!areFiltersOpened}
          onClear={showClearButton ? clearFilters : undefined}
          onClose={this.closePopup}
          onSubmit={applyFilters}
        >
          <FilterItemMobile title="Тип недвижимости" name={'ObjectType'} marginTop={0}>
            <MultiSelect
              name="ObjectType"
              options={OBJECT_TYPES}
              value={newobjectType}
              title="Тип недвижимости"
              placeholder="Любой"
              onChange={changeNewObjectTypes}
            />
          </FilterItemMobile>
          <FilterItemMobile title="Класс посёлка" name={'VillageClass'}>
            <MultiSelect
              options={VILLAGE_CLASSES}
              value={villageClass}
              title="Класс посёлка"
              placeholder="Любой"
              onChange={changeVillageClasses}
            />
          </FilterItemMobile>
          <FilterItemMobile title="Срок сдачи" name={'FinishDate'}>
            <SelectMobile
              options={FINISH_DATES}
              value={finishDate || ''}
              title="Срок сдачи"
              text={formatQuarterDate(finishDate) || 'Любой'}
              onChange={changeFinishDate}
            />
          </FilterItemMobile>
          <FilterItemMobile title="Цена" name={'Price'}>
            <RangeInput
              valueMin={priceFrom ?? undefined}
              valueMax={priceTo ?? undefined}
              min={0}
              max={1000000000}
              onChange={changePriceRangeFilter}
              postfix="₽"
            />
          </FilterItemMobile>
          <FilterItemMobile title="Расположение" name={'Location'}>
            <MobileLocationSwitcherContainer />
            {showRoadFilters && (
              <>
                <div className={styles['distance-input']}>
                  <RangeInput
                    valueMin={commuteKmFrom ?? undefined}
                    valueMax={commuteKmTo ?? undefined}
                    min={0}
                    max={9999}
                    onChange={changeCommuteDistance}
                    postfix="км"
                  />
                </div>
                <RadioButtonGroup
                  spliced
                  anyOption={false}
                  options={generateDistanceTypes(regionId ?? undefined)}
                  value={commuteDistanceType ?? ECommuteDistanceType.Road}
                  onChange={changeCommuteDistanceType}
                />
              </>
            )}
          </FilterItemMobile>
          {showBlackFridayFilter && (
            <FilterItemMobile title="Скидки" name={'Promotions'}>
              <div className={styles['black-friday-filter']}>
                <img src={blackFridayIcon} className={styles['black-friday-icon']} width={24} height={24} />
                <Checkbox
                  value="blackFriday"
                  checked={blackFriday}
                  onChange={changeBlackFridayFilter}
                  label="Чёрная пятница"
                />
              </div>
            </FilterItemMobile>
          )}
          <FilterItemMobile title="Статус продажи" name={'SaleStatus'}>
            <MultiSelect
              options={SALE_STATUSES}
              value={saleStatus || []}
              title="Статус продажи"
              placeholder="Любой"
              onChange={changeSaleStatus}
            />
          </FilterItemMobile>
          <FilterItemMobile title="Готовность посёлка" name={'ReadyStatus'}>
            <MultiSelect
              options={READY_STATUSES}
              value={readyStatus || []}
              title="Готовность посёлка"
              placeholder="Любая"
              onChange={changeReadyStatus}
            />
          </FilterItemMobile>
          <FilterItemMobile title="Материал дома" name={'HouseMaterial'}>
            <MultiSelect
              options={HOUSE_MATERIALS}
              value={houseMaterialType || []}
              title="Материал дома"
              placeholder="Любой"
              allOption
              onChange={changeHouseMaterials}
            />
          </FilterItemMobile>
          <FilterItemMobile title="Площадь дома" name={'HouseArea'}>
            <RangeInput
              valueMin={areaFrom ?? undefined}
              valueMax={areaTo ?? undefined}
              min={0}
              max={9999}
              onChange={changeAreaRange}
              postfix="м²"
            />
          </FilterItemMobile>
          <FilterItemMobile title="Этажей в доме" name={'FloorCount'}>
            <RangeInput
              valueMin={floorCountFrom ?? undefined}
              valueMax={floorCountTo ?? undefined}
              min={0}
              max={99}
              onChange={changeFloorCountRange}
            />
          </FilterItemMobile>
          <FilterItemMobile title="Отопление" name={'HeatingType'}>
            <MultiSelect
              options={HEATING_SOURCES}
              value={heatingSourceType || []}
              title="Отопление"
              placeholder="Любое"
              onChange={changeHeatingSourceType}
            />
          </FilterItemMobile>
          <FilterItemMobile title="Санузел" name={'WcLocationType'}>
            <CheckboxButtonGroup
              anyOption={false}
              options={WC_LOCATIONS}
              value={wcLocationType ? [wcLocationType] : []}
              onChange={changeWcLocationType}
            />
          </FilterItemMobile>
          <FilterItemMobile title="Коммуникации" name={'Communications'}>
            <MultiSelect
              options={OTHER_COMMUNICATIONS}
              value={otherCommunications || []}
              title="Коммуникации"
              placeholder="Любые"
              allOption
              onChange={changeOtherCommunications}
            />
          </FilterItemMobile>
        </MobileFiltersPopup>
      </UiKitContextProvider>
    );
  }

  private openPopup = () => this.props.showMobileFilters(true);

  private closePopup = () => {
    this.props.showMobileFilters(false);
  };
}
